import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../contexts/ToastContext';
import { apiClient } from '../../services/api';
import type { BatchDeleteStreamUpdate } from '../../services/api';

interface BatchDeleteProgressDialogProps {
  isOpen: boolean;
  title: string;
  confirmMessage: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
  progress: BatchDeleteStreamUpdate | null;
}

function BatchDeleteProgressDialog({
  isOpen,
  title,
  confirmMessage,
  onConfirm,
  onClose,
  progress,
}: BatchDeleteProgressDialogProps) {
  if (!isOpen) {
    return null;
  }

  const percent = progress && progress.total > 0 ? Math.min(100, Math.round((progress.deleted / progress.total) * 100)) : 0;
  const isCompleted = progress?.status === 'completed';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="events-batch-delete-title"
        className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800"
      >
        <h3 id="events-batch-delete-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {title}
        </h3>
        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{confirmMessage}</p>

        {progress && (
          <div className="mt-4 space-y-2">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Progress: {progress.deleted} / {progress.total}
            </p>
            <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700" aria-hidden="true">
              <div className="h-2 rounded-full bg-primary-600 transition-all" style={{ width: `${percent}%` }} />
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {isCompleted ? 'Deletion completed.' : 'Deleting events...'}
            </p>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          {(progress === null || isCompleted) && (
            <button
              type="button"
              className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              onClick={onClose}
            >
              {isCompleted ? 'Close' : 'Cancel'}
            </button>
          )}
          {progress === null && (
            <button
              type="button"
              className="btn-danger"
              onClick={() => {
                void onConfirm();
              }}
            >
              Delete Events
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function getErrorMessage(error: unknown): string {
  const apiError = error as { response?: { data?: { title?: string; detail?: string; message?: string } }; message?: string };
  return apiError.response?.data?.detail
    ?? apiError.response?.data?.title
    ?? apiError.response?.data?.message
    ?? apiError.message
    ?? 'Request failed';
}

export function EventsManagementPage() {
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<number | undefined>(undefined);
  const [olderThan, setOlderThan] = useState<string>('');
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [truncateProgress, setTruncateProgress] = useState<BatchDeleteStreamUpdate | null>(null);

  const previewQuery = useQuery({
    queryKey: ['systemEventsTruncatePreview', statusFilter ?? 'all', olderThan || 'none'],
    queryFn: () =>
      apiClient.previewTruncateEvents({
        statusFilter,
        olderThan: olderThan || undefined,
      }),
    enabled: false,
    retry: false,
  });

  const isAnyActionPending = truncateProgress !== null;

  const handlePreview = async () => {
    try {
      const result = await previewQuery.refetch();
      if (result.error) {
        addToast(`Failed to preview event deletion. ${getErrorMessage(result.error)}`, 'error');
        return;
      }

      const count = result.data?.count ?? 0;
      setPreviewCount(count);
      addToast(`Preview complete: ${count} event(s) will be deleted.`, 'info');
    } catch (error) {
      addToast(`Failed to preview event deletion. ${getErrorMessage(error)}`, 'error');
    }
  };

  const handleCloseDeleteDialog = () => {
    setShowDeleteConfirm(false);
    setTruncateProgress(null);
  };

  const handleStartEventsTruncate = async () => {
    try {
      setTruncateProgress({ total: previewCount ?? 0, deleted: 0, status: 'started' });
      await apiClient.truncateEventsStreaming(
        {
          statusFilter,
          olderThan: olderThan || undefined,
        },
        (progress) => {
          setTruncateProgress({
            total: progress.total,
            deleted: progress.deleted,
            status: progress.status,
          });
        },
      );
      addToast('Events deleted successfully.', 'success');
      setPreviewCount(null);
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['event'] });
      queryClient.invalidateQueries({ queryKey: ['events-by-incident'] });
    } catch (error) {
      addToast(`Failed to delete events. ${getErrorMessage(error)}`, 'error');
      setShowDeleteConfirm(false);
      setTruncateProgress(null);
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Events Management</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Manage system event retention and bulk deletion.
        </p>
      </header>

      <section className="space-y-4 rounded-lg border border-red-200 bg-white p-6 shadow-sm dark:border-red-900/50 dark:bg-gray-800">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-semibold text-gray-900 dark:text-gray-100">
            <span aria-hidden="true">⚠️</span>
            Truncate Events
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Permanently delete events from the database. This action cannot be undone.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="truncate-status-filter" className="label">Status Filter</label>
            <select
              id="truncate-status-filter"
              className="input"
              value={statusFilter === undefined ? '' : statusFilter}
              onChange={(event) => {
                const value = event.target.value;
                setStatusFilter(value === '' ? undefined : Number(value));
                setPreviewCount(null);
              }}
              disabled={isAnyActionPending}
            >
              <option value="">All</option>
              <option value="0">Pending</option>
              <option value="1">Processing</option>
              <option value="2">Processed</option>
              <option value="3">Failed</option>
              <option value="4">Duplicate</option>
              <option value="5">Suppressed</option>
            </select>
          </div>
          <div>
            <label htmlFor="truncate-older-than" className="label">Older Than</label>
            <input
              id="truncate-older-than"
              type="date"
              className="input"
              value={olderThan}
              onChange={(event) => {
                setOlderThan(event.target.value);
                setPreviewCount(null);
              }}
              disabled={isAnyActionPending}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="btn-secondary disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => {
              void handlePreview();
            }}
            disabled={previewQuery.isFetching || isAnyActionPending}
          >
            {previewQuery.isFetching ? 'Previewing...' : 'Preview'}
          </button>

          {previewCount !== null && (
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {previewCount} event(s) will be deleted.
            </p>
          )}

          <button
            type="button"
            className="btn-danger disabled:cursor-not-allowed disabled:opacity-50"
            disabled={previewCount === null || previewCount <= 0 || isAnyActionPending}
            onClick={() => {
              setTruncateProgress(null);
              setShowDeleteConfirm(true);
            }}
          >
            Delete {previewCount ?? 0} Events
          </button>
        </div>
      </section>

      <BatchDeleteProgressDialog
        isOpen={showDeleteConfirm}
        title="Delete Events?"
        confirmMessage={`You are about to permanently delete ${previewCount ?? 0} events from the database.`}
        onConfirm={handleStartEventsTruncate}
        onClose={handleCloseDeleteDialog}
        progress={truncateProgress}
      />
    </div>
  );
}
